import { getDeviceCapability } from './deviceCatalogCache';

/**
 * Whether a device is reporting, decided from the device catalog's per-type silence tolerance.
 *
 * Deliberately NOT a boolean. Across the platform the call sites disagree about what a MISSING
 * timestamp means, and each disagreement is load-bearing — a device card with no reading is a
 * fault, while a newly provisioned device with no sample must not appear in a list of things to go
 * and fix. Returning `unknown` keeps that decision at the call site, written down, instead of
 * buried in this function's default.
 *
 * tesseract's device-liveness surfaces are colour ramps and a debug filter rather than yes/no
 * questions, so what they consume is `deviceFreshness` below: HotDropStatusTab's timestamp labels,
 * GatewayReconPage's last-reported colour and DeviceOverviewDebug's seen-device filter.
 * `deviceOnlineState` is the same rule with one boundary, kept so the three clients share one API.
 */
export type OnlineState = 'online' | 'offline' | 'unknown';

/**
 * A liveness ramp from the same single tolerance, for surfaces that need more than one boundary.
 *
 * - `fresh`: silence <= tolerance (exactly when `deviceOnlineState` is `online`)
 * - `stale`: tolerance < silence <= DEAD_AFTER_TOLERANCES x tolerance
 * - `dead`: beyond that
 * - `unknown`: no usable timestamp (the same rule as `deviceOnlineState`). Render it grey, never
 *   green.
 *
 * Deriving the extra boundary instead of serving a second catalogue field keeps one number per
 * type, so a colour can never disagree with the offline verdict.
 */
export type Freshness = 'fresh' | 'stale' | 'dead' | 'unknown';

export const DEFAULT_OFFLINE_AFTER_MINUTES = 120;

/**
 * How many tolerances of silence make a device `dead` rather than `stale`. A product choice pending
 * ops sign-off: it moves every red boundary that `deviceFreshness` drives (for a 2-hour type, red
 * starts at 6 hours).
 */
export const DEAD_AFTER_TOLERANCES = 3;

/**
 * Best-known tolerances, applied until the catalog loads and for any type it does not carry, so
 * tesseract agrees with the other clients from first paint rather than only after the network
 * settles. Mostly this is the timeout map they used before this field existed.
 *
 * GENUS_INTELLILOG is the deliberate exception: 26 h is a NEW value. It is listed because the
 * catalog is served `max-age=3600`, so for up to an hour after an API deploy a browser can still
 * answer from a cached v5 catalog with no `reporting` block — and without this entry a once-daily
 * logger would keep reading offline through exactly that window.
 *
 * These numbers are the catalog's; when they diverge, the catalog wins.
 */
const FALLBACK_OFFLINE_AFTER_MINUTES: Record<string, number> = {
  DETECTIFY: 60 * 9,
  BOVE_BECO: 60 * 6, // reports 4-hourly
  GENUS_INTELLILOG: 60 * 26, // reports once daily; 24 h cadence + 2 h grace
};

/** How long this device type may be silent before it counts as offline, in minutes. */
export const offlineAfterMinutes = (deviceType?: string): number =>
  getDeviceCapability(deviceType)?.reporting?.offlineAfterMinutes ??
  FALLBACK_OFFLINE_AFTER_MINUTES[deviceType?.toUpperCase() ?? ''] ??
  DEFAULT_OFFLINE_AFTER_MINUTES;

/**
 * `unknown` when there is no usable timestamp — never coerced to "now". `moment(undefined)` is the
 * current time, so an absent timestamp compared that way silently reports a device that has never
 * transmitted as fresh; that exact bug is live in this repo's sibling clients today.
 *
 * Silence is compared strictly and in milliseconds, matching the API-side helper, so a device
 * exactly on a boundary is still on the healthier side of it and the implementations cannot
 * disagree.
 */
export const deviceFreshness = (lastSeenAt: string | number | Date | undefined | null, deviceType?: string): Freshness => {
  if (lastSeenAt === undefined || lastSeenAt === null || lastSeenAt === '') return 'unknown';

  const lastSeenMs = new Date(lastSeenAt).getTime();
  if (Number.isNaN(lastSeenMs)) return 'unknown';

  const silenceMs = Date.now() - lastSeenMs;
  const toleranceMs = offlineAfterMinutes(deviceType) * 60_000;
  if (silenceMs <= toleranceMs) return 'fresh';
  return silenceMs <= DEAD_AFTER_TOLERANCES * toleranceMs ? 'stale' : 'dead';
};

/** `deviceFreshness` with its two unhealthy states merged, so the two can never disagree. */
export const deviceOnlineState = (lastSeenAt: string | number | Date | undefined | null, deviceType?: string): OnlineState => {
  const freshness = deviceFreshness(lastSeenAt, deviceType);
  if (freshness === 'unknown') return 'unknown';
  return freshness === 'fresh' ? 'online' : 'offline';
};
